import { IonPage } from '@ionic/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { Responsive } from 'react-grid-layout'

import { DASH_GRID_LAYOUT } from '@apps-common/apps/portal/pages/dashboard/components/grid-layout'
import { DASH_PENDING_CANCELLATIONS_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/pending-cancellations/pending-cancellations-grid-layout'
import { PendingCancellationsCard } from '@apps-common/apps/portal/pages/dashboard/components/pending-cancellations/PendingCancellationsCard'
import { DASH_PINNED_UNITS_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/pinned-units/pinned-units-grid-layout'
import { PinnedUnitsCard } from '@apps-common/apps/portal/pages/dashboard/components/pinned-units/PinnedUnitsCard'
import { DASH_RECENT_ACTIVATIONS_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/recent-activations/recent-activations-grid-layout'
import { RecentActivationsCard } from '@apps-common/apps/portal/pages/dashboard/components/recent-activations/RecentActivationsCard'
import { DASH_RECENT_CANCELLATIONS_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/recent-cancellations/recent-cancellations-grid-layout'
import { RecentCancellationsCard } from '@apps-common/apps/portal/pages/dashboard/components/recent-cancellations/RecentCancellationsCard'
import { DASH_RECENT_ORDERS_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/recent-orders/recent-orders-grid-layout'
import { RecentOrdersCard } from '@apps-common/apps/portal/pages/dashboard/components/recent-orders/RecentOrdersCard'
import { DASH_TOTAL_ACTIVATIONS_KPI_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/total-activations-kpi/total-activations-kpi-grid-layout'
import { TotalActivationsKpiCard } from '@apps-common/apps/portal/pages/dashboard/components/total-activations-kpi/TotalActivationsKpiCard'
import { DASH_TOTAL_ACTIVE_KPI_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/total-active-kpi/total-active-kpi-grid-layout'
import { TotalActiveKpiCard } from '@apps-common/apps/portal/pages/dashboard/components/total-active-kpi/TotalActiveKpiCard'
import { DASH_TOTAL_CANCELLATIONS_KPI_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/total-cancellations-kpi/total-cancellations-kpi-grid-layout'
import { TotalCancellationsKpiCard } from '@apps-common/apps/portal/pages/dashboard/components/total-cancellations-kpi/TotalCancellationsKpiCard'
import { DASH_TOTAL_PENDING_CANCELLATION_KPI_GRID_CONFIG } from '@apps-common/apps/portal/pages/dashboard/components/total-pending-cancellation-kpi/total-pending-cancellation-kpi-grid-layout'
import { TotalPendingCancellationKpiCard } from '@apps-common/apps/portal/pages/dashboard/components/total-pending-cancellation-kpi/TotalPendingCancellationKpiCard'
import { RootHeaderButtons } from '@apps-common/components/common'
import { NavbarCompanyLogo } from '@apps-common/components/common/logos'
import { PageTitle } from '@apps-common/components/common/portal-title'
import { Header, LAYOUT_PADDING_TOP_DEFAULT, LayoutContainer, VertSpacerXl } from '@apps-common/components/layout'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { SizeContext, SizeProvider } from '@apps-common/contexts'
import { ROUTE_DASHBOARD } from '@apps-radionet-portal/config/private-routes'

import { commonIcons } from '@apps-common/config/icons'
import { getTimeGreeting } from '@apps-common/helpers/time-greeting'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import moment from 'moment'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const ResponsiveGridLayout = Responsive

export const DashboardPage = () => {
  const fbUser = usePortalFirebaseUser()
  const greeting = getTimeGreeting()
  const date = moment().format('dddd, D MMMM')

  return (
    <IonPage>
      <Header classes='max-w-[1650px]'>
        <Navbar startComponents={<NavbarCompanyLogo />} transparent icons={<RootHeaderButtons />} />
        <Breadcrumbs route={ROUTE_DASHBOARD} />
        <PageTitle classes='!leading-[1em] mt-[10px] flex flex-col items-start justify-start sm:flex-row sm:items-center sm:justify-between pr-[10px]'>
          <span className='leading-[1.8rem] sm:leading-[0px]'>
            {greeting}
            {fbUser?.firstName ? `, ${fbUser.firstName}` : ''}
          </span>
          <span className='text-text-muted jost-medium flex items-center justify-center gap-2 text-[0.9rem] sm:text-[20px] mt-[4px]'>
            <commonIcons.calendar className='text-[1.3em]' />
            {date}
          </span>
        </PageTitle>
      </Header>
      <LayoutContainer>
        <div className='safe-area-top' />
        <div className={classNames(LAYOUT_PADDING_TOP_DEFAULT, 'pt-[120px] md:w-[calc(100%-20px)] max-w-[1650px] scrollbar-none')}>
          {/* <PullRefresh loading={isLoading} callback={refetch} /> */}
          <AnimatePresence>
            <SizeProvider>
              <SizeContext.Consumer>
                {({ width }) => {
                  if (!width) return null

                  return (
                    <motion.div key='dash-grid-desktop' {...motionFadeIn(0.2)}>
                      <ResponsiveGridLayout
                        width={width}
                        useCSSTransforms={false}
                        margin={{ xl: [20, 20], lg: [20, 20], md: [15, 15], sm: [15, 15], xs: [0, 15], xxs: [0, 15] }}
                        layouts={DASH_GRID_LAYOUT}
                        breakpoints={{ xl: 1600, lg: 1200, md: 946, sm: 640, xs: 480, xxs: 0 }}
                        cols={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                      >
                        <div key={DASH_PINNED_UNITS_GRID_CONFIG.id}>
                          <PinnedUnitsCard />
                        </div>
                        <div key={DASH_RECENT_ORDERS_GRID_CONFIG.id} className='px-[15px] sm:p-0'>
                          <RecentOrdersCard />
                        </div>
                        <div key={DASH_RECENT_ACTIVATIONS_GRID_CONFIG.id}>
                          <RecentActivationsCard />
                        </div>
                        <div key={DASH_TOTAL_ACTIVE_KPI_GRID_CONFIG.id} className='pr-[7.5px] pl-[15px] sm:p-0'>
                          <TotalActiveKpiCard />
                        </div>
                        <div key={DASH_TOTAL_ACTIVATIONS_KPI_GRID_CONFIG.id} className='pl-[7.5px] pr-[15px] sm:p-0'>
                          <TotalActivationsKpiCard />
                        </div>
                        <div key={DASH_TOTAL_PENDING_CANCELLATION_KPI_GRID_CONFIG.id} className='pr-[7.5px] pl-[15px] sm:p-0'>
                          <TotalPendingCancellationKpiCard />
                        </div>
                        <div key={DASH_TOTAL_CANCELLATIONS_KPI_GRID_CONFIG.id} className='pl-[7.5px] pr-[15px] sm:p-0'>
                          <TotalCancellationsKpiCard />
                        </div>
                        <div key={DASH_PENDING_CANCELLATIONS_GRID_CONFIG.id}>
                          <PendingCancellationsCard />
                        </div>
                        <div key={DASH_RECENT_CANCELLATIONS_GRID_CONFIG.id}>
                          <RecentCancellationsCard />
                        </div>
                      </ResponsiveGridLayout>
                    </motion.div>
                  )
                }}
              </SizeContext.Consumer>
            </SizeProvider>
          </AnimatePresence>
        </div>
        <VertSpacerXl />
      </LayoutContainer>
    </IonPage>
  )
}
